import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';

import { useCustomer } from '../../customer';

export type CustomerSelectorProps = {
  variant?: 'light' | 'dark';
};

export const CustomerSelector = ({ variant = 'light' }: CustomerSelectorProps) => {
  const { customer, setCustomer, customers } = useCustomer();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (next: typeof customer) => {
    setCustomer(next);
    setAnchorEl(null);
  };

  const isDark = variant === 'dark';

  const triggerSx = isDark
    ? {
        gap: 0.75,
        py: 0.625,
        pl: 1.5,
        pr: 0.75,
        borderRadius: 999,
        backgroundColor: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(255,255,255,0.15)',
        color: 'white',
        transition: 'background 0.12s ease, border-color 0.12s ease',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.18)',
          borderColor: 'rgba(255,255,255,0.25)',
        },
        '&:focus-visible': {
          outline: '2px solid white',
          outlineOffset: 2,
        },
        ...(open && { backgroundColor: 'rgba(255,255,255,0.18)' }),
      }
    : {
        gap: 0.75,
        py: 0.5,
        px: 1.25,
        borderRadius: 1.25,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease',
        '&:hover': {
          backgroundColor: 'grey.50',
          borderColor: 'grey.400',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
        ...(open && { backgroundColor: 'grey.50', borderColor: 'grey.400' }),
      };

  const labelSx = isDark
    ? { fontWeight: 600, fontSize: 14, color: 'white', letterSpacing: '0.1px' }
    : {
        fontWeight: 700,
        fontSize: '1.5rem',
        letterSpacing: '-0.3px',
        color: 'text.primary',
      };

  const chevronSx = isDark
    ? { color: 'rgba(255,255,255,0.85)', fontSize: 18 }
    : { color: 'text.secondary', fontSize: 22 };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          ...triggerSx,
        }}
      >
        <Typography component="span" sx={labelSx}>
          {customer.name}
        </Typography>
        <ExpandMoreIcon sx={chevronSx} />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 220,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10)',
          },
        }}
      >
        {customers.map((c) => {
          const selected = c.id === customer.id;
          return (
            <MenuItem
              key={c.id}
              onClick={() => handleSelect(c)}
              selected={selected}
              sx={{
                fontSize: 14,
                fontWeight: selected ? 600 : 500,
                py: 1,
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 18,
                  display: 'inline-flex',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                {selected && <CheckIcon sx={{ fontSize: 16 }} />}
              </Box>
              {c.name}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
